import React, { useEffect, useMemo, useState } from "react";
import { getUsuarioLogadoAdaptado as getUsuarioLogado } from "../../utils/ids";
import { useNavigate } from "react-router-dom";
import "./DashboardAluno.scss";
import chevronIcon from "../../assets/ic_chevron.svg";
import AvisoDestaque from "../../components/AvisoDestaque/AvisoDestaque";
import { ocorrenciasCalendario } from "../../utils/eventosCalendario";
import { recarregarSeNecessario } from "../../services/eventosStore";

const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const DIAS_SEMANA = [
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
  "Domingo",
];

function parseLocalDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [y, m, d] = raw.slice(0, 10).split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return null;
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
}

function toDateKey(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getCalendarStart(date) {
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  const day = first.getDay();
  const offset = day === 0 ? 6 : day - 1;
  return new Date(first.getFullYear(), first.getMonth(), 1 - offset);
}

function addDays(date, amount) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);
}

function sameMonth(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function isPastDay(date) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return date < today;
}

export default function DashboardAluno() {
  const navigate = useNavigate();

  const [dataVersion, setDataVersion] = useState(0);
  const [mostrarSecundarias, setMostrarSecundarias] = useState(
    () => localStorage.getItem("menuAlunoSecundariasVisiveis") === "true",
  );

  useEffect(() => {
    // Garante que os eventos do backend estejam carregados no store.
    recarregarSeNecessario().catch(() => {});

    const bump = () => setDataVersion((v) => v + 1);
    const syncSecundarias = () => {
      setMostrarSecundarias(
        localStorage.getItem("menuAlunoSecundariasVisiveis") === "true",
      );
      bump();
    };

    window.addEventListener("app:data-changed", bump);
    window.addEventListener("eventos:changed", bump);
    window.addEventListener("menuAlunoSecundarias:changed", syncSecundarias);

    return () => {
      window.removeEventListener("app:data-changed", bump);
      window.removeEventListener("eventos:changed", bump);
      window.removeEventListener(
        "menuAlunoSecundarias:changed",
        syncSecundarias,
      );
    };
  }, []);

  const usuarioLogado = useMemo(() => {
    if (dataVersion < 0) return null;
    return getUsuarioLogado();
  }, [dataVersion]);

  // "+" leva ao fluxo de responsável; só aparece para aluno que também é
  // responsável de alguma turma (fonte: backend /usuarios/me).
  const isResponsavel =
    Array.isArray(usuarioLogado?.turmasComoResponsavel) &&
    usuarioLogado.turmasComoResponsavel.length > 0;

  const hoje = new Date();
  const primeiroDiaMesAtual = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const [mesAtual, setMesAtual] = useState(primeiroDiaMesAtual);

  // Agrupa as bolinhas (uma por evento×turma) por dia. dataVersion força o
  // recálculo quando o store ou o toggle de secundárias muda.
  const eventosPorData = useMemo(() => {
    const map = new Map();
    const ocorrencias = ocorrenciasCalendario(usuarioLogado, {
      mostrarSecundarias,
    });

    ocorrencias.forEach((oc) => {
      const data = parseLocalDate(oc.data);
      if (!data) return;
      const key = toDateKey(data);

      if (!map.has(key)) map.set(key, { eventos: [], dots: [] });
      const atual = map.get(key);
      atual.eventos.push(oc);
      atual.dots.push({
        turmaId: oc.turmaId,
        eventoId: oc.eventoId,
        cor: oc.cor,
        nome: oc.turmaNome,
        visto: oc.vistoCalendario,
      });
    });

    return map;
    // usuarioLogado muda de referência a cada atualização do store (bump),
    // o que dispara a releitura de getEventos() aqui dentro.
  }, [usuarioLogado, mostrarSecundarias]);

  const dias = useMemo(() => {
    const start = getCalendarStart(mesAtual);
    return Array.from({ length: 35 }, (_, index) => addDays(start, index));
  }, [mesAtual]);

  const isMesAtualReal =
    mesAtual.getFullYear() === primeiroDiaMesAtual.getFullYear() &&
    mesAtual.getMonth() === primeiroDiaMesAtual.getMonth();

  function handleNextMonth() {
    setMesAtual((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }

  function handlePrevMonth() {
    if (isMesAtualReal) return;
    setMesAtual((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }

  function handleDayClick(date, canClick) {
    if (!canClick) return;
    navigate(`/detalhe-calendario-aluno/${toDateKey(date)}`);
  }

  return (
    <>
      <AvisoDestaque usuario={usuarioLogado} />
      <div className="ver-calendario-aluno">
        <div className="ver-calendario-aluno__header">
          <div className="ver-calendario-aluno__titleWrap">
            <button
              type="button"
              className={`ver-calendario-aluno__navBtn ${
                isMesAtualReal ? "is-disabled" : ""
              }`}
              onClick={handlePrevMonth}
              aria-label="Mês anterior"
              disabled={isMesAtualReal}
            >
              <img src={chevronIcon} alt="" className="mes_anterior" />
            </button>

            <h1 className="ver-calendario-aluno__title">
              {MESES[mesAtual.getMonth()]} {mesAtual.getFullYear()}
            </h1>

            <button
              type="button"
              className="ver-calendario-aluno__navBtn"
              onClick={handleNextMonth}
              aria-label="Próximo mês"
            >
              <img src={chevronIcon} alt="" />
            </button>
          </div>

          {isResponsavel && (
            <button
              type="button"
              className="ver-calendario-aluno__plus"
              aria-label="Acessar fluxo de responsável"
              onClick={() => navigate("/criar-evento")}
            >
              +
            </button>
          )}
        </div>

        <div className="ver-calendario-aluno__weekdays">
          {DIAS_SEMANA.map((dia) => (
            <div key={dia} className="ver-calendario-aluno__weekday">
              {dia}
            </div>
          ))}
        </div>

        <div className="ver-calendario-aluno__grid">
          {dias.map((dia) => {
            const key = toDateKey(dia);
            const info = eventosPorData.get(key);

            const foraDoMes = !sameMonth(dia, mesAtual);
            const passado = isPastDay(dia);

            const dots = info?.dots || [];
            const sortedDots = [...dots].sort((a, b) => {
              if (a.visto !== b.visto) return a.visto ? 1 : -1;
              return 0;
            });

            const visibleDots = sortedDots.slice(0, 4);
            const extraCount = dots.length > 4 ? dots.length - 4 : 0;

            const canClick = !passado && (info?.eventos?.length || 0) > 0;

            return (
              <button
                key={key}
                type="button"
                className={[
                  "ver-calendario-aluno__cell",
                  foraDoMes ? "is-outside" : "",
                  passado ? "is-past" : "",
                  canClick ? "is-clickable" : "",
                  dots.length ? "has-events" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => handleDayClick(dia, canClick)}
                disabled={!canClick}
              >
                <span className="ver-calendario-aluno__dayNumber">
                  {dia.getDate()}
                </span>

                {!passado && dots.length > 0 && (
                  <div className="ver-calendario-aluno__dots">
                    {visibleDots.map((dot) => (
                      <span
                        key={`${key}-${dot.turmaId}-${dot.eventoId}`}
                        className={`ver-calendario-aluno__dot ${
                          dot.visto ? "is-visto" : ""
                        }`}
                        style={{ backgroundColor: dot.cor }}
                        title={dot.nome}
                      >
                        {dot.visto && "✓"}
                      </span>
                    ))}

                    {extraCount > 0 && (
                      <span className="ver-calendario-aluno__more">
                        +{extraCount}
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
